import { useRef, useEffect, useState } from "react";
import mapboxGL, { Map } from "mapbox-gl";
import { Commune } from "../commune-interface";
import { useReset } from "../context/ResetContext";
import { Box, IconButton } from '@mui/material';
import MyLocationTwoToneIcon from '@mui/icons-material/MyLocationTwoTone';

mapboxGL.accessToken = process.env.REACT_APP_MAPBOX_TOKEN as string;

export default function MapComponent({ communes }: { communes: Commune[] }) {  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const mapContainer = useRef(null);
  const mapRef = useRef<Map | null>(null);
  const originalLng = -61;
  const originalLat = 14.6;
  const originalZoom = isMobile ? 9.24 : 10.8; // Adjust zoom for mobile
  const { reset } = useReset();
  
  // Add resize listener for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (mapRef.current) {
        mapRef.current.resize();
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Add the recenter function
  const recenterMap = () => {
    if (mapRef.current) {
      mapRef.current.flyTo({
        center: [originalLng, originalLat],
        zoom: originalZoom,
      });
    }
  };

  useEffect(() => {
    if (!mapContainer.current) {
      return;
    }

    if (!mapRef.current) {
      const map = new Map({
        container: mapContainer!.current!,
        style: "mapbox://styles/fhocs/clnxdzgt6002i01qxew2l0wm3",
        center: [originalLng, originalLat],
        zoom: originalZoom,
        minZoom: 5,
      });      map.on("move", () => {
        // We can track position changes here if needed in the future
      });

      map.on("style.load", () => {
        communes.forEach((commune) => {
          map.addSource(commune.code.toString(), {
            type: "geojson",
            data: {
              type: "FeatureCollection",
              features: [
                {
                  type: "Feature",
                  geometry: {
                    type: commune.zone.type,
                    coordinates: commune.zone.coordinates,
                  },
                  properties: {
                    name: commune.name,
                  },
                },
                {
                  type: "Feature",
                  geometry: {
                    type: "Point",
                    coordinates: commune.mairiePos.coordinates,
                  },
                  properties: {
                    name: `Mairie de ${commune.name}`,
                  },
                },
              ],
            },
          });

          map.addLayer({
            id: `Contour de ${commune.code}`,
            type: "line",
            source: commune.code.toString(),
            layout: {
              "line-join": "round",
              "line-cap": "round",
            },
            paint: {
              "line-color": "#877b59",
              "line-width": 1,
            },
            filter: ["==", "$type", "Polygon"],
          });

          map.addLayer({
            id: `Fill de ${commune.code}`,
            type: "fill",
            source: commune.code.toString(),
            paint: {
              "fill-color": "#5cb85c",
              "fill-opacity": 0.2,
            },
            layout: {
              visibility: !!commune.timeFound ? "visible" : "none",
            },
            filter: ["==", "$type", "Polygon"],
          });

          map.addLayer({
            id: `Mairie de ${commune.code}`,
            type: "symbol",
            source: commune.code.toString(),
            layout: {
              visibility: !!commune.timeFound ? "visible" : "none",
              "icon-image": "town-hall", // reference the image
              "icon-size": 1,
              "text-field": commune.name,
              "text-variable-anchor": ["top", "bottom", "left", "right"],
              "text-radial-offset": 0.5,
              "text-justify": "auto",
            },
            filter: ["==", "$type", "Point"],
          });
        });
      });
      mapRef.current = map;
    }
    const foundCommunes = communes.filter((c) => !!c.timeFound);
    foundCommunes.forEach((commune) => {
      mapRef.current?.setLayoutProperty(
        `Fill de ${commune.code}`,
        "visibility",
        "visible"
      );
      mapRef.current?.setLayoutProperty(
        `Mairie de ${commune.code}`,
        "visibility",
        "visible"
      );
    });
  }, [communes, originalLng, originalZoom]);

  useEffect(() => {
    if (reset && mapRef.current) {
      communes.forEach((commune) => {
        mapRef.current?.setLayoutProperty(
          `Fill de ${commune.code}`,
          "visibility",
          "none"
        );
        mapRef.current?.setLayoutProperty(
          `Mairie de ${commune.code}`,
          "visibility",
          "none"
        );
      });
    }
  }, [reset, communes]);
  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <Box
        ref={mapContainer}
        sx={{
          height: { xs: '60vh', md: '100vh' },
          width: '100%',
          position: 'relative'
        }}
      />
      <Box 
        sx={{ 
          position: 'absolute', 
          bottom: { xs: 16, md: 24 }, 
          right: { xs: 16, md: 24 },
          zIndex: 5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}
      >
        <IconButton 
          onClick={recenterMap} 
          aria-label="RecenterMap"
          sx={{ 
            backgroundColor: 'background.paper', 
            boxShadow: 2,
            '&:hover': { 
              backgroundColor: 'background.paper',
              opacity: 0.9 
            }
          }}
        >
          <MyLocationTwoToneIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
